import React from 'react'
import { useFormContext } from 'react-hook-form'
import { Typography } from '@grammarly/badger-ui'

import { ContentProps } from '../../../renderer'
import { TypeComponentTextInput } from '../../../../lib/types/component-text-input'
import { useUserService } from '../../../../lib/hooks'
import { useError } from '../../../inputs/hooks'
import { errorStyle } from '../../../ui-lib/form/error-text'
import { TextLimitConfig, charactersToWords, getMaxCharacterLimit } from '../../../inputs/validations'
import { CharacterCount } from '../../../inputs/character-count'
import { WordCount } from '../../../inputs/word-count'
import { calculateWordCount } from '../../../../lib/utils/strings'

import styles from './side-by-side-form.module.scss'

const { C4 } = Typography

type SideBySideErrorOrCounterProps = ContentProps<TypeComponentTextInput>

export const SideBySideErrorOrCounter: React.FC<SideBySideErrorOrCounterProps> = ({
  structure: {
    fields: { name, maxLength, anonymousCharacterLimit, useWordLimit, hasLongformText },
  },
}) => {
  const { isAnonymous } = useUserService().currentUser
  const {
    formState: { isDirty, isSubmitted },
    watch,
  } = useFormContext()
  const value = watch(name)

  const textLimitConfig: TextLimitConfig = {
    maxCharacterLimit: getMaxCharacterLimit(maxLength, hasLongformText),
    showErrorText: false,
    anonymousCharacterLimit,
    useWordLimit,
  }

  const error = useError(name, textLimitConfig)
  const hasErrors = !!error && (isDirty || isSubmitted)
  if (!hasErrors) {
    if (!value) {
      return null
    }

    const LengthCounter = useWordLimit ? WordCount : CharacterCount
    const maxChars =
      textLimitConfig.anonymousCharacterLimit && isAnonymous
        ? textLimitConfig.anonymousCharacterLimit
        : textLimitConfig.maxCharacterLimit

    const isAboveLimit = useWordLimit
      ? calculateWordCount(value) > maxChars / charactersToWords
      : value.length > maxChars

    return (
      <C4 as="div" className={isAboveLimit ? errorStyle : styles.counter}>
        <LengthCounter value={value} maxChars={maxChars} />
      </C4>
    )
  }

  return (
    <C4 as="div" className={errorStyle}>
      {error}
    </C4>
  )
}
